import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  Calendar, 
  Clock, 
  User, 
  Mail, 
  MessageSquare, 
  CheckCircle2,
  AlertCircle,
  Loader2
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { de } from "date-fns/locale";
import { usePublicSlotBooking } from "@/hooks/useCalendarAvailabilitySlots";

const PublicBooking = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { slotInfo, isLoading, error, bookPublicSlot } = usePublicSlotBooking(token || '');

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [bookingResult, setBookingResult] = useState<{
    slot_title?: string;
    start_time?: string;
    end_time?: string;
  } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const result = await bookPublicSlot.mutateAsync({ name, email, message });
      setBookingResult({
        slot_title: result.slot_title,
        start_time: result.start_time,
        end_time: result.end_time,
      });
      setIsSuccess(true);
    } catch (err) {
      // Error is handled by the mutation
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">Lade Termindetails...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error || !slotInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle>Zeitfenster nicht gefunden</CardTitle>
            <CardDescription>
              Dieser Buchungslink ist ungültig oder das Zeitfenster ist nicht mehr verfügbar.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button variant="outline" onClick={() => navigate('/')}>
              Zur Startseite
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Not available state
  if (!slotInfo.is_available) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-warning/10 flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-warning" />
            </div>
            <CardTitle>Nicht mehr verfügbar</CardTitle>
            <CardDescription>
              Dieses Zeitfenster ist leider bereits ausgebucht oder abgelaufen.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm font-medium">{slotInfo.title || 'Terminbuchung'}</p>
              <p className="text-sm text-muted-foreground">
                {format(parseISO(slotInfo.start_time), "EEEE, dd. MMMM yyyy", { locale: de })}
              </p>
              <p className="text-sm text-muted-foreground">
                {format(parseISO(slotInfo.start_time), "HH:mm", { locale: de })} - {format(parseISO(slotInfo.end_time), "HH:mm", { locale: de })}
              </p>
            </div>
            <Button variant="outline" onClick={() => navigate('/')}>
              Zur Startseite
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (isSuccess && bookingResult) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
              <CheckCircle2 className="h-10 w-10 text-primary" />
            </div>
            <CardTitle className="text-2xl">Termin gebucht!</CardTitle>
            <CardDescription>
              Ihre Buchung wurde erfolgreich durchgeführt.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-muted rounded-lg space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-primary" />
                <span className="font-medium">{bookingResult.slot_title || 'Terminbuchung'}</span>
              </div>
              {bookingResult.start_time && (
                <>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    <span>
                      {format(parseISO(bookingResult.start_time), "EEEE, dd. MMMM yyyy", { locale: de })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span className="ml-6">
                      {format(parseISO(bookingResult.start_time), "HH:mm", { locale: de })} - {format(parseISO(bookingResult.end_time || bookingResult.start_time), "HH:mm", { locale: de })}
                    </span>
                  </div>
                </>
              )}
              <div className="flex items-center gap-2 text-sm text-muted-foreground pt-2 border-t">
                <User className="h-4 w-4" />
                <span>{name}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Mail className="h-4 w-4" />
                <span>{email}</span>
              </div>
            </div>
            <Alert>
              <AlertDescription>
                Sie erhalten in Kürze eine Bestätigung per E-Mail an <strong>{email}</strong>.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Booking form
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Calendar className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-2xl">{slotInfo.title || 'Terminbuchung'}</CardTitle>
          <CardDescription>
            Buchen Sie einen Termin bei <strong>{slotInfo.owner_name}</strong>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Slot details */}
          <div className="p-4 bg-muted rounded-lg space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4 text-primary" />
              <span className="font-medium">
                {format(parseISO(slotInfo.start_time), "EEEE, dd. MMMM yyyy", { locale: de })}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span>
                {format(parseISO(slotInfo.start_time), "HH:mm", { locale: de })} - {format(parseISO(slotInfo.end_time), "HH:mm", { locale: de })}
              </span>
            </div>
            {slotInfo.max_capacity > 1 && (
              <div className="text-xs text-muted-foreground pt-1 border-t mt-2">
                {slotInfo.current_bookings}/{slotInfo.max_capacity} Plätze vergeben
              </div>
            )}
          </div>

          {/* Booking form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Name *
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ihr vollständiger Name"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                E-Mail *
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ihre@email.de"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="message" className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Nachricht (optional)
              </Label>
              <Textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Gibt es etwas, das wir im Voraus wissen sollten?"
                rows={3}
              />
            </div>

            <Button 
              type="submit" 
              className="w-full" 
              size="lg"
              disabled={bookPublicSlot.isPending || !name || !email}
            >
              {bookPublicSlot.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Wird gebucht...
                </>
              ) : (
                'Termin buchen'
              )}
            </Button>
          </form>

          <p className="text-xs text-center text-muted-foreground">
            Mit der Buchung stimmen Sie zu, dass Ihre Daten zur Terminvereinbarung verwendet werden.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default PublicBooking;
